import { useTheme } from '@backoffice-frontend/patterns'
import { Switch, Text } from '@moia-dev/pace-web'

type OverlayToggleProps = {
  label: string
  color?: string
  checked: boolean
  count?: number
  /** When set, displays as routeCount/count instead of just count */
  routeCount?: number
  onChange: (checked: boolean) => void
  /** PNG/image URL for the legend icon. Takes priority over iconSvg. */
  iconSrc?: string
  /** Inline SVG string for the legend icon. Falls back to a colored square. */
  iconSvg?: string
  /**
   * Shape of the `color` swatch. Square by default (how the ODD element rows
   * draw it); `'circle'` matches layers whose map markers are round, so the
   * swatch reads as a legend for the marker rather than for an area. The circle
   * is also drawn smaller — a 12px round swatch reads as a chunky bullet next
   * to 12px label text, where an area swatch is meant to look like a fill.
   */
  colorShape?: 'square' | 'circle'
  /** Hide the icon/square entirely */
  hideIcon?: boolean
  bold?: boolean
  /** Use a native checkbox instead of a Switch */
  useCheckbox?: boolean
  /** Show a spinner overlay over the checkbox/switch while fetching */
  loading?: boolean
}

export const OverlayToggle = ({
  label,
  color,
  checked,
  count,
  routeCount,
  onChange,
  iconSrc,
  iconSvg,
  colorShape = 'square',
  hideIcon,
  bold,
  useCheckbox,
  loading,
}: OverlayToggleProps) => {
  const theme = useTheme()
  // Both sizes sit inside the 16px line box of `Text size="sm"`, so neither
  // shape can stretch a row taller than its label.
  const colorSwatchSize = colorShape === 'circle' ? 8 : 12
  return (
    <div
      css={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '3px 0',
      }}
    >
      <div css={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {!hideIcon &&
          (iconSrc ? (
            <img
              src={iconSrc}
              alt={label}
              css={{
                width: 22,
                height: 22,
                flexShrink: 0,
                objectFit: 'contain',
              }}
            />
          ) : iconSvg ? (
            <div
              css={{ width: 18, height: 18, flexShrink: 0 }}
              // biome-ignore lint/security/noDangerouslySetInnerHtml: static SVG from ODD_ICON_DEFS
              dangerouslySetInnerHTML={{ __html: iconSvg }}
            />
          ) : (
            <div
              css={{
                width: colorSwatchSize,
                height: colorSwatchSize,
                borderRadius: colorShape === 'circle' ? '50%' : 2,
                backgroundColor: color,
                flexShrink: 0,
              }}
            />
          ))}
        <Text size="sm" weight={bold ? 'bold' : undefined}>
          {label}
          {count != null && (
            <span
              css={{
                color: theme.semantic.ColorContentReduced,
                fontWeight: 400,
              }}
            >
              {' '}
              ({routeCount != null ? `${routeCount}/` : ''}
              {count})
            </span>
          )}
        </Text>
      </div>
      {loading ? (
        <div
          css={{
            width: 20,
            // Switch small: 20px content + 1px border×2 (content-box) + 2px margin×2 = 26px total.
            // Checkbox is 16px so 20px container already covers it.
            height: useCheckbox ? 20 : 26,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            css={{
              animation: 'overlay-toggle-spin 0.7s linear infinite',
              '@keyframes overlay-toggle-spin': {
                to: { transform: 'rotate(360deg)' },
              },
            }}
          >
            <circle
              cx="10"
              cy="10"
              r="8"
              fill="none"
              stroke={theme.semantic.ColorContentReduced}
              strokeWidth="2.5"
              strokeDasharray="34 14"
            />
          </svg>
        </div>
      ) : useCheckbox ? (
        <input
          type="checkbox"
          // The label is a sibling `<Text>` rather than a wrapping `<label>`, so
          // without this the row is announced as an unnamed checkbox — the
          // `Switch` branch below already carries the same aria-label.
          aria-label={label}
          checked={checked}
          onChange={e => onChange(e.target.checked)}
          css={{
            width: 16,
            height: 16,
            cursor: 'pointer',
            flexShrink: 0,
            accentColor: theme.semantic.ColorAccentDefault,
          }}
        />
      ) : (
        <Switch
          aria-label={label}
          size="small"
          checked={checked}
          onChange={onChange}
        />
      )}
    </div>
  )
}
